import type {
  AreaLoadMatrixRow,
  AreaOverview,
  Dimension,
  EnergyResilienceAnalysisDetailResponse,
  EnergyResilienceAnalysisTreeNode,
  LoadRangeKey,
  PeriodRange,
  RealtimeTrendPoint,
} from './types';

interface MockDimensionConfig {
  dimension: Dimension;
  count: number;
  names: string[];
}

const ELECTRICITY_DIMENSIONS: MockDimensionConfig[] = [
  { dimension: 'plant', count: 3, names: ['T1主变', 'T2主变', 'T3主变'] },
  {
    dimension: 'station',
    count: 5,
    names: ['1#二级配电站', '2#二级配电站', '3#二级配电站', '4#二级配电站', '5#二级配电站'],
  },
  {
    dimension: 'area',
    count: 86,
    names: ['1#三级变电站', '2#三级变电站', '4#三级变电站', '3#三级变电站', '5#三级变电站'],
  },
  {
    dimension: 'line',
    count: 1268,
    names: ['1#动力出线', '2#动力出线', '3#动力出线', '4#动力出线', '5#动力出线'],
  },
];

const GAS_DIMENSIONS: MockDimensionConfig[] = [
  { dimension: 'plant', count: 1, names: ['全厂'] },
  {
    dimension: 'station',
    count: 2,
    names: ['1#天然气站', '2#天然气站'],
  },
  {
    dimension: 'outlet',
    count: 7,
    names: ['1#天然气出口', '2#天然气出口', '3#天然气出口', '4#天然气出口', '5#天然气出口'],
  },
  {
    dimension: 'area',
    count: 38,
    names: ['1#供能区域', '2#供能区域', '3#供能区域', '4#供能区域', '5#供能区域'],
  },
];

const createTree = (): EnergyResilienceAnalysisTreeNode[] =>
  Array.from({ length: 6 }, (_, mediumType) => {
    const dimensions = mediumType === 0 ? ELECTRICITY_DIMENSIONS : GAS_DIMENSIONS;
    return {
      id: mediumType + 1,
      mediumType,
      children: dimensions.map((item, dimensionIndex) => ({
        id: mediumType * 1000 + dimensionIndex * 100 + 1,
        dimension: item.dimension,
        count: item.count,
        children: item.names.map((name, rowIndex) => ({
          id: mediumType * 1000 + dimensionIndex * 100 + rowIndex + 1,
          name,
          dimension: item.dimension,
        })),
      })),
    };
  });

export const MOCK_RESILIENCE_TREE = createTree();

const getPeriodScale = (periodRange: PeriodRange) => {
  const periodDays = Math.max(1, Math.round((periodRange.endTime - periodRange.startTime) / 86_400_000));
  if (periodDays <= 1) return 24 / 8760;
  if (periodDays <= 31) return periodDays / 365;
  return 1;
};

export const getMockResilienceDetail = (
  node: EnergyResilienceAnalysisTreeNode,
  mediumType: number,
  periodRange: PeriodRange,
  revision = 0
): EnergyResilienceAnalysisDetailResponse => {
  const seed = Number(node.id ?? 0) + mediumType * 13 + revision;
  const periodScale = getPeriodScale(periodRange);
  const lowPercent = 84 + (seed % 6) / 10;
  const midPercent = 9 + (seed % 4) / 10;
  const highPercent = Number((100 - lowPercent - midPercent).toFixed(1));
  const annualBaseHours = 960 + (seed % 7) * 18 + revision * 3;
  const totalMinutes = annualBaseHours * periodScale * 60;
  const maxLoadRate = 52 + (seed % 37) + ((seed * 7) % 10) / 10;

  return {
    configId: node.id,
    name: node.name,
    realtimeValue: 3200 + (seed % 900),
    ratedValue: 5000,
    realtimeLoadRate: 55 + (seed % 35),
    avgLoadRate: Number((18.5 + (seed % 6) * 0.6).toFixed(1)),
    maxLoadRate,
    minLoadRate: 12 + (seed % 18),
    maxLoadTime: '2026-05-06 16:59',
    rangeDurations: [
      { range: 'low', rangeName: '低负荷', duration: (totalMinutes * lowPercent) / 100, durationPercent: lowPercent },
      { range: 'mid', rangeName: '常用负荷', duration: (totalMinutes * midPercent) / 100, durationPercent: midPercent },
      {
        range: 'high',
        rangeName: '较高负荷',
        duration: (totalMinutes * highPercent) / 100,
        durationPercent: highPercent,
      },
    ],
  };
};

export const getMockRealtimeTrend = (mediumType: number, revision = 0): RealtimeTrendPoint[] =>
  Array.from({ length: 25 }, (_, index) => {
    const seed = mediumType * 7 + revision;
    const wave = Math.sin((index + seed) / 2.8) * 2.6;
    const drift = index > 6 && index < 16 ? 2.8 : 0;
    const loadRate = Number((63 + wave + drift + ((index + seed) % 4) * 0.35).toFixed(1));
    return {
      time: index === 0 ? '0:00' : `${index}:00`,
      loadRate,
      referenceRate: 65,
    };
  });

const AREA_NAMES = [
  '切制中心',
  '包装车间',
  '制丝车间',
  '动力中心',
  '仓储中心',
  '联合工房',
  '辅料库',
  '办公区',
  '污水站',
  '空压站',
  '锅炉房',
];

const AREA_MEDIUMS = [
  { mediumType: 1, mediumName: '天然气' },
  { mediumType: 2, mediumName: '压缩空气' },
  { mediumType: 4, mediumName: '氧气' },
  { mediumType: 3, mediumName: '二氧化碳' },
  { mediumType: 5, mediumName: '氩气' },
];

const LOAD_RANGE_SEQUENCE: LoadRangeKey[] = ['low', 'mid', 'high'];

export const getMockAreaOverview = (revision = 0): AreaOverview => ({
  totalAreas: 11,
  highAreaCount: 4,
  focusArea: revision % 2 ? '动力中心' : '切制中心',
  focusDuration: Number((325.12 + revision * 3.4).toFixed(2)),
  focusRatio: Number((27.6 + (revision % 3) * 0.4).toFixed(1)),
  focusMedium: '压缩空气',
});

export const getMockAreaMatrix = (revision = 0): AreaLoadMatrixRow[] =>
  AREA_NAMES.map((name, areaIndex) => ({
    key: `area-${areaIndex}`,
    name,
    tag: '生产区域',
    stats: AREA_MEDIUMS.map((medium, mediumIndex) => {
      const seed = areaIndex * 5 + mediumIndex + revision;
      const low = Number((72.8 + (seed % 5) * 0.35).toFixed(1));
      const mid = Number((15.1 + (seed % 4) * 0.25).toFixed(1));
      const high = Number((100 - low - mid).toFixed(1));
      const durationBase = 560 + (areaIndex % 4) * 8 + mediumIndex * 5;
      const percents: Record<LoadRangeKey, number> = { low, mid, high };
      return {
        ...medium,
        ranges: LOAD_RANGE_SEQUENCE.reduce(
          (result, range) => ({
            ...result,
            [range]: {
              percent: percents[range],
              duration: range === 'low' ? durationBase : range === 'mid' ? 22 + (seed % 5) : 9 + (seed % 4),
              dailyAverage: range === 'low' ? 21.4 : range === 'mid' ? 0.8 : 0.4,
            },
          }),
          {} as AreaLoadMatrixRow['stats'][number]['ranges']
        ),
      };
    }),
  }));
